import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { format } from "date-fns";
import {
  genTemplateBodyDateTime,
  genTemplateBodyNumber,
} from "@components/utils/TableTemplateBodyGenerator";
import SignatureButton from "../../../components/signature-button/SignatureButton";
import useIsMobile from "../../../hooks/useIsMobile";
import { useAPIGetKartuStokMinyak } from "../api/api-get-kartu-stok-minyak";
import { useAPIVerifKartuStokMinyak } from "../api/api-verify-kartu-stok-minyak";
import KartuStokMinyakFilter from "./kartu-stok-minyak-filter";

const formatLiter = (value) =>
  `${Number(value ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} L`;

/**
 * The paper form TRAN-DOC-001 on screen: one sheet = one month of one department + jenis minyak.
 * The first line is what was left of the month before (saldo awal), the last line the totals.
 */
const KartuStokMinyakList = forwardRef((props, ref) => {
  const isMobile = useIsMobile();
  const { onClickRowDelete, onClickRowEdit, onClickAddNew } = props;

  const {
    loading,
    listData,
    saldoAkhir,
    totalMasuk,
    totalKeluar,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleOnPatchViewRow,
  } = useAPIGetKartuStokMinyak();

  const { verifSetting, fetchVerifSetting, verifyPnj } =
    useAPIVerifKartuStokMinyak({
      onVerified: (id, responseData) => {
        if (responseData) {
          handleOnPatchViewRow({
            id,
            verif_pnj_id: responseData.verif_pnj_id,
            verif_pnj_datetime: responseData.verif_pnj_datetime,
            data_verif_pnj: responseData.data_verif_pnj,
          });
        }
      },
    });

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => {
      handleOnUpdateViewRow(row);
    },
    handleOnDeleteViewRow: (row) => {
      handleOnDeleteViewRow(row);
    },
  }));

  // use effect section

  useEffect(() => {
    fetchVerifSetting();
  }, []);

  // open the sheet as soon as department, jenis minyak and month are known
  useEffect(() => {
    fetchListData();
  }, [filterValue.department_id, filterValue.jenis_minyak_id, filterValue.bulan]);

  const hasSheet = !!filterValue.department_id && !!filterValue.jenis_minyak_id;

  // the lines of the sheet (the first one is the stok awal the PNJ typed) and the totals
  const sheetRows = hasSheet
    ? [
        ...listData,
        ...(listData.length > 0
          ? [
              {
                id: "total",
                isTotal: true,
                masuk: totalMasuk,
                keluar: totalKeluar,
                sisa: saldoAkhir,
              },
            ]
          : []),
      ]
    : [];

  // the masuk created from a permintaan (auto) and the verified rows are locked
  const isLocked = (row) =>
    (row.permintaan_minyak_id && row.permintaan_minyak_id > 0) ||
    (row.verif_pnj_id && row.verif_pnj_id > 0);

  const isVirtual = (row) => row.isTotal;

  // kind: "awal" = the stock counted by hand (stok awal), "keluar" = fuel for an alat berat.
  // Stock that arrives with a permintaan minyak is recorded by the backend on "Diterima";
  // the dialog still lets the user switch to a manual stok datang.
  const handleOnClickAddNew = (kind) => {
    // a new line goes to the sheet that is open
    if (onClickAddNew) {
      onClickAddNew({
        ...(kind === "awal"
          ? {
              mode: "awal",
              jenis_mutasi: "masuk",
              stok_fisik: null,
              // no line in this month yet: the keterangan tells the stok awal and last month
              sheet_kosong: listData.length === 0,
            }
          : { jenis_mutasi: kind }),
        department_id: filterValue.department_id,
        data_department: filterValue.data_department,
        jenis_minyak_id: filterValue.jenis_minyak_id,
        data_jenis_minyak: filterValue.data_jenis_minyak,
      });
    }
  };

  //  custom ui section

  const noBodyTemplate = (row) => (isVirtual(row) ? "" : row.no);

  const tanggalBodyTemplate = (row) => {
    if (row.isTotal) return <span className="font-bold">Total</span>;
    return genTemplateBodyDateTime({ row, field: "tanggal_waktu" });
  };

  const alatBeratBodyTemplate = (row) => {
    if (!row.data_alat_berat) return "";
    const { kode_unit, nama } = row.data_alat_berat;
    return [kode_unit, nama].filter(Boolean).join(" - ");
  };

  const operatorBodyTemplate = (row) =>
    row.operator_nama ?? row.data_operator?.nama ?? "";

  // a cell without a value shows "-" (the line of the totals keeps its numbers)
  const emptyDash = (row, value) => (row.isTotal ? "" : value || "-");

  const alatBeratCell = (row) => emptyDash(row, alatBeratBodyTemplate(row));

  const operatorCell = (row) => emptyDash(row, operatorBodyTemplate(row));

  const masukBodyTemplate = (row) =>
    !row.isTotal && !(Number(row.masuk) > 0) ? (
      "-"
    ) : (
      <span className={row.isTotal ? "font-bold" : ""}>
        {genTemplateBodyNumber({ row, field: "masuk", maxFraction: 2 })}
      </span>
    );

  const keluarBodyTemplate = (row) =>
    !row.isTotal && !(Number(row.keluar) > 0) ? (
      "-"
    ) : (
      <span className={row.isTotal ? "font-bold" : ""}>
        {genTemplateBodyNumber({ row, field: "keluar", maxFraction: 2 })}
      </span>
    );

  const sisaBodyTemplate = (row) => (
    <span className="font-bold">
      {genTemplateBodyNumber({ row, field: "sisa", maxFraction: 2 })}
    </span>
  );

  const keteranganBodyTemplate = (row) => {
    if (row.isTotal) {
      return <span className="text-slate-500">Saldo akhir bulan ini</span>;
    }
    return (
      <div className="col-start-start">
        <span>{row.keterangan || "-"}</span>
        {row.data_permintaan_minyak?.nomor && (
          <span className="text-xs text-indigo-500">
            Ref. {row.data_permintaan_minyak.nomor}
          </span>
        )}
      </div>
    );
  };

  const pnjBodyTemplate = (row) => {
    if (isVirtual(row)) return "";

    if (row.verif_pnj_id) {
      return (
        <div className="col-center-center gap-1">
          <i className="pi pi-verified text-xl text-blue-500" />
          <span className="text-sm text-blue-700 text-center">
            {row.data_verif_pnj?.nama || "PNJ"}
          </span>
        </div>
      );
    }

    return (
      <SignatureButton
        className="max-h-[2rem] !px-4 bg-emerald-500 border-emerald-500"
        rounded={true}
        label="PNJ"
        title="PNJ"
        jabatanId={verifSetting.jabatan_id}
        jabatanIdRequirement={verifSetting["pnj"]}
        data={row}
        onConfirm={(confirmProps) => {
          verifyPnj({
            id: row.id,
            username: confirmProps?.username,
            password: confirmProps?.password,
          });
        }}
      />
    );
  };

  const actionBodyTemplate = (row) => {
    if (isVirtual(row)) return "";
    const locked = isLocked(row);

    return (
      <div className="row-center-center gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          disabled={locked}
          className="bg-indigo-400 max-w-[1.85rem] max-h-[1.85rem]"
          onClick={() => {
            if (onClickRowEdit) onClickRowEdit(row);
          }}
          pt={{ icon: { className: "!text-xs" } }}
        />
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          disabled={locked}
          className="bg-red-400 max-w-[1.85rem] max-h-[1.85rem]"
          onClick={() => {
            if (onClickRowDelete) onClickRowDelete(row);
          }}
          pt={{ icon: { className: "!text-xs" } }}
        />
      </div>
    );
  };

  const rowClassName = (row) => (row.isTotal ? "bg-indigo-50" : "");

  const mobileBodyTemplate = (row) => {
    if (row.isTotal) {
      return (
        <div className="col-start-start w-full gap-1">
          <span className="font-bold text-slate-500">Total bulan ini</span>
          <div className="row-between-center w-full">
            <span className="text-slate-500 text-sm">
              Masuk <strong>{formatLiter(row.masuk)}</strong>
            </span>
            <span className="text-slate-500 text-sm">
              Keluar <strong>{formatLiter(row.keluar)}</strong>
            </span>
          </div>
          <div className="row-between-center w-full">
            <span className="text-slate-500 text-sm">Saldo akhir</span>
            <span className="font-bold text-emerald-600">{formatLiter(row.sisa)}</span>
          </div>
        </div>
      );
    }

    const isMasuk = row.jenis_mutasi === "masuk";

    return (
      <div className="col-start-start pb-1 w-full">
        <div className="row-between-center w-full">
          <Tag
            value={isMasuk ? "Masuk" : "Keluar"}
            severity={isMasuk ? "success" : "warning"}
          />
          <span className="font-semibold text-slate-400 text-sm italic">
            {row.tanggal_waktu
              ? format(new Date(row.tanggal_waktu), "dd/MM/yyyy HH:mm")
              : ""}
          </span>
        </div>
        <span className="font-bold text-slate-500 pt-2">
          {isMasuk
            ? row.data_permintaan_minyak?.nomor
              ? `Ref. ${row.data_permintaan_minyak.nomor}`
              : "Stok masuk"
            : alatBeratBodyTemplate(row) || "-"}
        </span>
        {!isMasuk && operatorBodyTemplate(row) && (
          <div className="row-start-center w-full gap-2 pt-2">
            <i className="pi pi-user text-slate-400" />
            <span className="text-slate-500 text-sm">
              Operator: {operatorBodyTemplate(row)}
            </span>
          </div>
        )}
        <div className="row-start-center w-full gap-2 pt-2">
          <i className="pi pi-gauge text-slate-400" />
          <span className="text-slate-500 text-sm">
            {isMasuk ? "Masuk" : "Keluar"}{" "}
            <strong>{formatLiter(isMasuk ? row.masuk : row.keluar)}</strong>
            {" · "}Sisa <strong>{formatLiter(row.sisa)}</strong>
          </span>
        </div>
        {row.keterangan && (
          <div className="row-start-center w-full gap-2 pt-2">
            <i className="pi pi-comment text-slate-400" />
            <span className="text-slate-500 text-sm">{row.keterangan}</span>
          </div>
        )}
        <div className="row-between-center w-full gap-2 pt-2">
          <div className="row-start-center gap-2">
            <Button
              icon="pi pi-pencil"
              label="Edit"
              rounded
              disabled={isLocked(row)}
              className="bg-indigo-400 max-h-[2rem] !px-4"
              onClick={() => {
                if (onClickRowEdit) onClickRowEdit(row);
              }}
              pt={{ icon: { className: "!text-xs" } }}
            />
            {!row.verif_pnj_id && pnjBodyTemplate(row)}
          </div>
          {row.verif_pnj_id && (
            <div className="row-start-center gap-2">
              <i className="pi pi-verified text-blue-500" />
              <span className="text-slate-500 text-sm">
                {row.data_verif_pnj?.nama ?? "PNJ"}
              </span>
            </div>
          )}
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            disabled={isLocked(row)}
            className="bg-red-400 max-w-[2rem] max-h-[2rem]"
            onClick={() => {
              if (onClickRowDelete) onClickRowDelete(row);
            }}
            pt={{ icon: { className: "!text-xs" } }}
          />
        </div>
      </div>
    );
  };

  const bulanLabel = filterValue.bulan
    ? new Date(filterValue.bulan).toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <KartuStokMinyakFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleOnClickRefresh}
        />
      </div>

      {/* sheet section */}
      <div
        className={`elevated-card-tight-vertical ${
          isMobile ? "col-start-center" : "col-start-end"
        } w-full gap-2`}
      >
        <div className="row-between-start w-full gap-2">
          <div className="col-start-start">
            <span className="font-bold text-lg text-slate-600">
              Kartu Stok Minyak
            </span>
            {hasSheet && (
              <>
                <span className="text-sm text-slate-500">
                  {filterValue.data_department?.nama} ·{" "}
                  {filterValue.data_jenis_minyak?.nama}
                </span>
                <span className="text-sm text-slate-500">{bulanLabel}</span>
              </>
            )}
          </div>
          <span className="text-sm font-semibold text-slate-400">TRAN-DOC-001</span>
        </div>

        <div className="row-between-center w-full flex-wrap gap-2">
          <div className="row-start-center flex-wrap gap-2">
            <Button
              icon="pi pi-plus"
              label="Stok Awal"
              severity="success"
              onClick={() => handleOnClickAddNew("awal")}
            />
            <Button
              icon="pi pi-minus"
              label="Catat Pengeluaran"
              onClick={() => handleOnClickAddNew("keluar")}
            />
          </div>
          {hasSheet && saldoAkhir !== null && (
            <div className="row-start-center gap-2">
              <span className="text-sm font-semibold text-slate-500">
                Saldo akhir
              </span>
              <span className="text-lg font-bold text-emerald-600">
                {formatLiter(saldoAkhir)}
              </span>
            </div>
          )}
        </div>

        {!hasSheet && (
          <div className="col-center-center w-full gap-1 py-6 text-slate-400">
            <i className="pi pi-book text-3xl" />
            <span className="text-sm text-center">
              Pilih department, jenis minyak, dan bulan untuk membuka kartu stok.
            </span>
          </div>
        )}

        {hasSheet && !isMobile && (
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={sheetRows}
            rowClassName={rowClassName}
            emptyMessage={
              loading
                ? ""
                : "Belum ada transaksi bulan ini. Mulai dengan Stok Awal."
            }
          >
            <Column
              header="No"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
              body={noBodyTemplate}
            />
            <Column
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[8rem]"
              body={actionBodyTemplate}
            />
            <Column header="Tgl / Waktu" body={tanggalBodyTemplate} />
            <Column header="Alat Berat" body={alatBeratCell} />
            <Column header="Operator" body={operatorCell} />
            <Column align={"right"} header="Masuk (L)" body={masukBodyTemplate} />
            <Column align={"right"} header="Keluar (L)" body={keluarBodyTemplate} />
            <Column align={"right"} header="Sisa (L)" body={sisaBodyTemplate} />
            <Column header="PNJ" align={"center"} body={pnjBodyTemplate} />
            <Column header="Keterangan" body={keteranganBodyTemplate} />
          </DataTable>
        )}
        {hasSheet && isMobile && (
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={sheetRows}
            rowClassName={rowClassName}
            emptyMessage={
              loading
                ? ""
                : "Belum ada transaksi bulan ini. Mulai dengan Stok Awal."
            }
          >
            <Column header="Kartu Stok" body={mobileBodyTemplate} />
          </DataTable>
        )}
      </div>
    </div>
  );
});

export default KartuStokMinyakList;
